import { RtcSignalingClient } from "@formant/core/client/RtcSignalingClient";
import { defined } from "@formant/core/common/defined";
import { getSessionType } from "@formant/core/common/getSessionType";
import { getSessionUserAndDevicePeer } from "@formant/core/common/getSessionUserAndDevicePeer";
import { somePeer } from "@formant/core/common/somePeer";
import { IDeviceDetails } from "@formant/core/model/IDeviceDetails";
import { IRtcPeer } from "@formant/core/model/IRtcPeer";
import { IRtcSession } from "@formant/core/model/IRtcSession";

import { IAuthStore } from "./IAuthStore";
import { IDeviceDetailsStore } from "./IDeviceDetailsStore";
import { IRtcSignalingStore } from "./IRtcSignalingStore";
import { StoreCache } from "./StoreCache";

export class RtcSignalingStore implements IRtcSignalingStore {
    private peerCache = new StoreCache<void, IRtcPeer[]>();
    private sessionCache = new StoreCache<void, IRtcSession[]>();

    constructor(
        private rtcSignalingClient: RtcSignalingClient,
        private authStore: IAuthStore,
        private deviceDetailsStore: IDeviceDetailsStore,
    ) {}

    peers(): IRtcPeer[] | undefined {
        return this.peerCache.get(undefined, () => this.fetchPeers());
    }

    sessions(): IRtcSession[] | undefined {
        return this.sessionCache.get(undefined, () => this.fetchSessions());
    }

    peerDevices({ onlyTeleopable }: { onlyTeleopable?: boolean } = {}):
        | IDeviceDetails[]
        | undefined {
        const peers = this.peers();
        const devices = this.deviceDetailsStore.list({
            enabled: true,
            type: "default",
        });
        if (!peers || !devices) {
            return undefined;
        }

        const peerDeviceIds = new Set(
            peers
                .filter(_ => _.deviceId)
                .filter(_ =>
                    onlyTeleopable
                        ? _.capabilitySet.streaming &&
                          _.capabilitySet.streaming.streams.length > 0
                        : true,
                )
                .map(_ => defined(_.deviceId)),
        );

        return devices.filter(_ => peerDeviceIds.has(defined(_.id)));
    }

    ownerPeerForDeviceTeleop(device: IDeviceDetails): IRtcPeer | undefined {
        const sessions = this.sessions()?.filter(
            _ => getSessionType(_) === "teleop",
        );
        if (!sessions) {
            return;
        }

        const teleopSession = sessions.find(_ =>
            somePeer(_, peer => peer.deviceId === device.id),
        );
        if (!teleopSession) {
            return;
        }

        const peers = getSessionUserAndDevicePeer(teleopSession);
        if (!peers) {
            return;
        }

        const { userPeer } = peers;
        return userPeer;
    }

    async fetchPeers(): Promise<IRtcPeer[]> {
        const peers = await this.rtcSignalingClient.getPeers(
            await this.authStore.token(),
        );
        this.peerCache.set(undefined, peers);
        return peers;
    }

    async fetchSessions(): Promise<IRtcSession[]> {
        const sessions = await this.rtcSignalingClient.getSessions(
            await this.authStore.token(),
        );
        this.sessionCache.set(undefined, sessions);
        return sessions;
    }
}
